import { IComponentOptions, IScope, ITimeoutService, equals } from "angular";
import { StateService } from "@uirouter/angularjs";
import { ITraceRootScopeService } from "@gtmhub/core/tracing";
import dayjs from "@gtmhub/dayjs";
import { IEmployee } from "@gtmhub/employees";
import { IIndicator, UIErrorHandlingService } from "@gtmhub/error-handling";
import { IGoal } from "@gtmhub/goals/models";
import { IHomeCollapsedItems, IHomeTeam, IHomeTodoItems } from "@gtmhub/home/controllers/home";
import { HomeService, IHomeMetric } from "@gtmhub/home/services/home";
import { IGtmhubRootScopeService } from "@gtmhub/models";
import { GoalsChangedEventArgs, OkrEvents } from "@gtmhub/okrs";
import { IMetric } from "@gtmhub/okrs/metrics";
import { ISessionsIdMap } from "@gtmhub/sessions";
import { blurFocusedElement } from "@gtmhub/shared/dom";
import { toAssignee } from "@gtmhub/shared/utils";
import { ITask, ITaskCreatedArgs, ITaskDeletedArgs, ITaskUpdatedArgs, TaskEvents, TaskService } from "@gtmhub/tasks";
import { ReflectionFormComponent } from "@webapp/reflections/components/reflection-form/reflection-form.component";
import { ITodoReflection } from "@webapp/reflections/models/reflections.models";
import { getMockedKrUpdates } from "@webapp/reflections/services/mocks";
import { UiModalService } from "@webapp/ui/modal/services/modal.service";

type ITodoItemBase<B, T> = B & { deadline: string; type: T; rawDate?: string };
type ITaskTodoItem = ITodoItemBase<ITask, "task">;
type IMetricTodoItem = ITodoItemBase<IHomeMetric, "metric">;
type IGoalTodoItem = ITodoItemBase<IGoal, "goal">;
export type IReflectionTodoItem = ITodoItemBase<ITodoReflection, "reflection">;
export type ITodoItem = ITaskTodoItem | IMetricTodoItem | IGoalTodoItem;

interface IHomeTodoBindings {
  items: IHomeTodoItems;
  reflections: ITodoReflection[];
  teams: IHomeTeam[];
  employee: IEmployee;
  activeSessionsMap: ISessionsIdMap;
  isCollapsed: boolean;
  updateCollapse?(item: { [key: string]: Partial<IHomeCollapsedItems> }): void;
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface HomeTodoComponentCtrl extends IHomeTodoBindings {}
export class HomeTodoComponentCtrl {
  todoItems: ITodoItem[];
  todoReflections: IReflectionTodoItem[];
  indicatorsMap: {
    todoItems: IIndicator[];
    todoReflections: IIndicator[];
  };

  private taskEvents: TaskEvents;
  private okrEvents: OkrEvents;
  private strings = {
    deadlines: {
      overdue: "overdue",
      tomorrow: "tomorrow",
      today: "today",
      yesterday: "yesterday",
      missingDeadline: "missingDeadline",
    },
    statuses: {
      todo: "todo",
      inprogress: "inprogress",
      done: "done",
    },
    taskStatuses: ["inprogress", "todo"],
    states: {
      metricUpdate: "gtmhub.home._updateMetric.update",
      goalReview: "gtmhub.home._goalSidebar.goal._review.review",
      taskReview: "gtmhub.home._task.task",
      metric: "gtmhub.home._metric.metric",
      goalSidebar: "gtmhub.home._goalSidebar.goal",
      msteams: {
        metric: "gtmhub.msTeamsHome._metric.metric",
        metricUpdate: "gtmhub.msTeamsHome._updateMetric.update",
        taskReview: "gtmhub.msTeamsHome._task.task",
        goalReview: "gtmhub.msTeamsHome._goalSidebar.goal._review.review",
        goalSidebar: "gtmhub.msTeamsHome._goalSidebar.goal",
      },
    },
    actions: {
      approve: "approve",
    },
    attainmentTypeString: {
      average_children: "average_children",
      average_kr: "average_kr",
    },
  };

  public static $inject = ["TaskService", "UIErrorHandlingService", "$state", "$rootScope", "HomeService", "$timeout", "$scope", "UiModalService"];

  constructor(
    private tasksService: TaskService,
    private uiErrorHandlingService: UIErrorHandlingService,
    private $state: StateService,
    private $rootScope: IGtmhubRootScopeService & ITraceRootScopeService,
    private homeService: HomeService,
    private $timeout: ITimeoutService,
    private $scope: IScope,
    private modalService: UiModalService
  ) {
    this.okrEvents = new OkrEvents(this.$scope);
    this.taskEvents = new TaskEvents(this.$scope);
  }

  public $onInit(): void {
    this.okrEvents.onGoalsChanged((args) => this.onGoalsChanged(args));

    this.$scope.$on("homeTodoItemsUpdated", (evt, args: Record<"oldTodoMetrics", IHomeMetric[]>) => this.onItemsUpdated(args));
    this.$scope.$on("approvedGoal", (evt, goal) => this.onGoalApproved(goal));
    this.$scope.$on("goalDeleted", (evt, goal) => this.onGoalDeletion(goal));
    this.taskEvents.onTaskCreated((args) => this.onTaskCreated(args));
    this.taskEvents.onTaskDeleted((args) => this.onTaskDeleted(args));
    this.taskEvents.onTaskUpdated((args) => this.onTaskUpdated(args));

    this.todoItems = this.getEnrichedTodoItems();
    this.todoReflections = this.enrichReflections(this.reflections);
    this.indicatorsMap = {
      todoItems: [],
      todoReflections: [],
    };
  }

  public toggleTaskEditMode(item: ITodoItem & { showInput?: boolean }): void {
    if (item.showInput) {
      item.showInput = false;
      setTimeout(blurFocusedElement);
    } else {
      item.showInput = true;
      setTimeout(() => document.getElementById(item.id).focus());
    }
  }

  public updateTaskName(task: ITask, newName: string, taskIdx: string, event: Event): void {
    event.stopPropagation();
    event.preventDefault();

    this.tasksService.update(task.id, { name: newName }).then(
      () => {
        task.name = newName;
        setTimeout(blurFocusedElement);
      },
      (error) => this.uiErrorHandlingService.handleIndicator(error, { indicator: this.indicatorsMap.todoItems[taskIdx] })
    );
  }

  public completeTask(task: ITask, taskIdx: number): void {
    this.$rootScope.traceAction("complete_task", () => {
      this.indicatorsMap.todoItems[taskIdx] = { progress: true };
      this.tasksService.update(task.id, { status: this.strings.statuses.done }).then(
        (updatedTask) => {
          this.todoItems = this.todoItems.filter((item) => task.id !== item.id);
          delete this.indicatorsMap.todoItems[taskIdx];
          this.$rootScope.$broadcast("todoTaskDeleted", updatedTask);
          this.broadcastIfTodoItemsEmptied();
        },
        (error) => {
          this.indicatorsMap.todoItems[taskIdx] = { error };
        }
      );
    });
  }

  public updateMetric(metric: IMetric): void {
    if (this.$rootScope.isMsTeamsState) {
      this.$state.go(this.strings.states.msteams.metricUpdate, { metricId: metric.id });
      return;
    }

    this.$state.go(this.strings.states.metricUpdate, { metricId: metric.id });
  }

  public approveGoal(goal: IGoal): void {
    if (this.$rootScope.isMsTeamsState) {
      this.$state.go(this.strings.states.msteams.goalReview, { id: goal.id, action: this.strings.actions.approve });
      return;
    }

    this.$state.go(this.strings.states.goalReview, { id: goal.id, action: this.strings.actions.approve });
  }

  public toggleSection(): void {
    this.isCollapsed = !this.isCollapsed;
    if (this.updateCollapse) {
      this.updateCollapse({ item: { todo: this.isCollapsed } });
    }
  }

  public openTask(task: ITask): void {
    if (this.$rootScope.isMsTeamsState) {
      this.$state.go(this.strings.states.msteams.taskReview, { taskId: task.id });
      return;
    }

    this.$state.go(this.strings.states.taskReview, { taskId: task.id });
  }

  public openMetric(item: IHomeMetric): void {
    if (this.$rootScope.isMsTeamsState) {
      this.$state.go(this.strings.states.msteams.metric, { metricId: item.id });
      return;
    }

    this.$state.go(this.strings.states.metric, { metricId: item.id });
  }

  public openGoal(item: IGoal): void {
    if (this.$rootScope.isMsTeamsState) {
      this.$state.go(this.strings.states.msteams.goalSidebar, { id: item.id });
      return;
    }

    this.$state.go(this.strings.states.goalSidebar, { id: item.id });
  }

  private isTaskTodo(task: ITask, sessionId: string | null): boolean {
    if (!task) {
      return false;
    }

    const ownerIsCurrentUser = task.ownerId === this.employee.id;
    const statusIsNotDone = task.status !== this.strings.statuses.done;
    const belongsToNoSessionOrToActiveSession = !sessionId || this.activeSessionsMap[sessionId];
    const isNotArchived = !task.archived;

    return ownerIsCurrentUser && statusIsNotDone && belongsToNoSessionOrToActiveSession && isNotArchived;
  }

  private convertTaskToTodo(task: ITask): ITaskTodoItem {
    return { ...task, type: "task", deadline: this.calcDeadline(task.dueDate) };
  }

  private onMetricUpdated(metric: IMetric): void {
    this.todoItems = this.todoItems.filter((item) => item.id !== metric.id);
    this.broadcastIfTodoItemsEmptied();
  }

  private filterTodoItems(itemId: string, conditionCallback?: (item: ITodoItem) => boolean): ITodoItem[] {
    if (conditionCallback) {
      return this.todoItems.filter(conditionCallback);
    }

    return this.todoItems.filter((item) => item.id !== itemId);
  }

  private onMetricDeletion(metricId: string): void {
    this.todoItems = this.filterTodoItems(metricId);
  }

  private onMetricPatched(metric: Partial<IMetric>): void {
    const todoMetric = this.todoItems.find((item) => item.type === "metric" && item.id === metric.id);
    if (todoMetric) {
      Object.assign(todoMetric, metric);
    }
  }

  private enrichTasks(tasks: ITask[]): ITodoItem[] {
    return tasks.map<ITaskTodoItem>((task: ITask) => {
      return { ...task, type: "task", deadline: this.calcDeadline(task.dueDate), rawDate: task.dueDate };
    });
  }

  private enrichReflections(reflections: ITodoReflection[]): IReflectionTodoItem[] {
    return reflections.map((reflection) => {
      const team = this.teams.find((t) => t.id === reflection.teamId);
      return { ...reflection, type: "reflection", name: team?.name, deadline: this.calcDeadline(reflection.dueDate), rawDate: reflection.dueDate };
    });
  }

  public fillInReflection = (item: ITodoReflection): void => {
    const team = this.teams.find((t) => t.id === item.teamId);
    const teamReflectionQuestions = this.todoReflections.find((reflection) => reflection.teamId === team.id);

    this.modalService.create({
      uiContent: ReflectionFormComponent,
      uiWidth: 600,
      uiComponentParams: {
        krUpdates: getMockedKrUpdates(),
        questions: teamReflectionQuestions.questions.map((q) => ({ ...q, answer: "" })),
        team: toAssignee({ assignee: team, type: "team" }),
        title: team.name,
        timeframe: "Week Jul 25th - Jul 31th",
      },
      uiFooter: null,
    });
  };

  private enrichMetrics(metrics: IHomeMetric[]): ITodoItem[] {
    return metrics.map<IMetricTodoItem>((metric: IHomeMetric) => {
      return { ...metric, type: "metric", deadline: this.calcDeadline(metric.cadanceDeadline), rawDate: metric.cadanceDeadline };
    });
  }

  private enrichGoalsToApprove(goals: IGoal[]): ITodoItem[] {
    return goals.map<IGoalTodoItem>((goal: IGoal & { deadline: string }) => {
      return { ...goal, type: "goal", deadline: this.calcDeadline(goal.deadline), rawDate: goal.deadline };
    });
  }

  private calcDeadline(date: string) {
    const dateHoursSetToZero = dayjs(date).clone().hour(0).minute(0).second(0);
    const today = new Date().setHours(0, 0, 0, 0);

    const isToday = date && dateHoursSetToZero.diff(today, "days") === 0;

    if (date) {
      if (isToday) {
        return this.strings.deadlines.today;
      }
      return dateHoursSetToZero.from(today);
    }

    return "";
  }

  private onTaskCreated(args: ITaskCreatedArgs): void {
    if (this.isTaskTodo(args.task, args.sessionId)) {
      this.addTaskToItems(args.task);
    }
  }

  private onTaskDeleted(args: ITaskDeletedArgs): void {
    // isTaskTodo method could be used as well, added items iteration instead for extra safety
    if (this.todoItems.some((toDoItem) => toDoItem.id === args.task.id)) {
      this.todoItems = this.todoItems.filter((toDoItem) => toDoItem.id !== args.task.id);
      this.$rootScope.$broadcast("todoTaskDeleted");
    }
  }

  private onTaskUpdated(args: ITaskUpdatedArgs): void {
    const toDoTaskToUpdate = this.todoItems.find((toDoItem) => toDoItem.id === args.task.id);
    const isUpdatedTaskTodo = this.isTaskTodo(args.task, args.sessionId);

    // if task is already in todo collection
    if (toDoTaskToUpdate) {
      if (isUpdatedTaskTodo) {
        // convertTaskToTodo returns new object and the reference to the existing is lost without extend
        angular.extend(toDoTaskToUpdate, this.convertTaskToTodo(args.task));
      } else {
        this.todoItems = this.todoItems.filter((toDoItem) => toDoItem.id !== toDoTaskToUpdate.id);
        this.$rootScope.$broadcast("todoTaskDeleted");
      }
      // if task is not in to do collection
    } else {
      if (isUpdatedTaskTodo) {
        this.addTaskToItems(args.task);
      }
    }
  }

  private addTaskToItems(task: ITask): void {
    // newTodoItem - adds animation to the newly created task
    const enrichedTodoItem = this.convertTaskToTodo({ ...task, newTodoItem: true });
    this.todoItems.push(enrichedTodoItem);
    this.$rootScope.$broadcast("todoTaskAdded");

    // clear styles
    this.$timeout(() => {
      delete enrichedTodoItem.newTodoItem;
    }, 2000);
  }

  private onGoalApproved(approvedGoal: IGoal): void {
    this.todoItems = this.filterTodoItems(approvedGoal.id);
    this.broadcastIfTodoItemsEmptied();
  }

  private onGoalDeletion(deletedGoalId: string): void {
    this.todoItems = this.filterTodoItems(null, (item: ITodoItem) => {
      return item.type !== "metric" || item.goalId !== deletedGoalId;
    });
    this.todoItems = this.filterTodoItems(deletedGoalId);
  }

  private onGoalPatched(editedGoal: IGoal) {
    this.todoItems = this.todoItems.reduce<ITodoItem[]>((filteredItems, item) => {
      if (item.type === "metric" && item.goalId === editedGoal.id && editedGoal.attainmentTypeString === this.strings.attainmentTypeString.average_children) {
        return filteredItems;
      }
      if (item.id === editedGoal.id) {
        filteredItems.push({ ...editedGoal, type: "goal", deadline: this.calcDeadline((editedGoal as IGoal & Record<"deadline", string>).deadline) } as ITodoItem);
      } else {
        filteredItems.push(item);
      }

      return filteredItems;
    }, []);
  }

  private onMetricCreation(metric: IHomeMetric): void {
    const metricToCheck: IHomeMetric = { ...metric, sessionId: metric.sessionId };
    const isNotUpdated = this.homeService.getNotUpdatedMetricsInCurrentCadence([metricToCheck], this.activeSessionsMap, this.employee)[0]; // filtering metrics not updated in current cadance
    if (isNotUpdated) {
      const todoItem: ITodoItem = { ...metric, type: "metric", deadline: this.calcDeadline(metric.cadanceDeadline) };
      this.todoItems.push(todoItem);
    }
  }

  private onGoalsChanged(args: GoalsChangedEventArgs): void {
    switch (args.reason) {
      case "metricCreated":
        this.onMetricCreation(args.detail.metric as IHomeMetric);
        break;
      case "metricPatched":
        this.onMetricPatched(args.detail.metric);
        break;
      case "metricUpdated":
        this.onMetricUpdated(args.detail.metric);
        break;
      case "metricDeleted":
        this.onMetricDeletion(args.detail.metricId);
        break;
      case "goalPatched":
        this.onGoalPatched(args.detail.goal);
        break;
    }
  }

  private onItemsUpdated(args: Record<"oldTodoMetrics", IHomeMetric[]>): void {
    if (!equals(this.items.metrics, args.oldTodoMetrics)) {
      this.todoItems = this.getEnrichedTodoItems();
      this.broadcastIfTodoItemsEmptied();
    }
  }

  private getEnrichedTodoItems(): ITodoItem[] {
    return this.enrichTasks(this.items.tasks).concat(this.enrichMetrics(this.items.metrics)).concat(this.enrichGoalsToApprove(this.items.goalsToApprove)) as ITodoItem[];
  }

  private broadcastIfTodoItemsEmptied(): void {
    if (this.todoItems.length < 1) {
      this.$rootScope.$broadcast("toDoItemsEmptied");
    }
  }
}

export const HomeTodoComponent: IComponentOptions = {
  controller: HomeTodoComponentCtrl,
  templateUrl: require("./home-todo.component.html"),
  bindings: {
    items: "<",
    reflections: "<",
    teams: "<",
    employee: "<",
    activeSessionsMap: "<",
    isCollapsed: "<",
    updateCollapse: "&",
  },
};
